import { Robot, InstalledComponent } from '@/entities/Robot';
import { Enemy } from '@/entities/Enemy';
import { TargetModifier } from '@/behaviors/BehaviorDefs';
import { ComponentType, ComponentDef, WeaponTarget, ProjectileType } from '@/components/ComponentDefs';
import { ProjectileManager } from '@/systems/ProjectileManager';
// stabilityAccuracySpread removed — all projectiles always launch
import { EventBus, GameEvents } from '@/events/EventBus';
import { Actor } from '@/entities/Actor';
import { SoundManager } from '@/systems/SoundManager';
import { GOD } from '@/config/GodMode';
import { SpatialHash } from '@/systems/SpatialHash';
import { BehaviorContext } from '@/behaviors/BehaviorTypes';
import { hasLineOfSight } from '@/systems/LineOfSight';

// ── Cooldown tracking for weapons ──
export const fireCooldowns = new Map<string, number>(); // cdKey -> ms remaining

// ── Burst cannon state tracking ──
// Tracks how many shots remain in the current burst and cooldown between bursts
const burstState = new Map<string, {
  shotsLeft: number; burstCooldown: number; burstCritActive?: boolean;
  heat: number; overheated: boolean; overheatTimer: number;
}>();
const BURST_COUNT = 3;          // shots per burst
const BURST_INTERVAL_MS = 100;  // ms between burst shots
const BURST_PAUSE_MS = 1200;    // ms pause between bursts
// ── Overheat system ──
const BURST_HEAT_PER_SHOT = 12;     // heat added per shot
const BURST_HEAT_MAX = 100;          // overheat threshold (~8-9 shots = 3 bursts)
const BURST_HEAT_DECAY = 15;         // heat decay per second when not firing
const BURST_OVERHEAT_COOLDOWN = 3.0; // seconds of forced cooldown when overheated

// ── Critical hit system: deterministic "Precision Score" ──

const CRIT_THRESHOLD = 0.65;

const WEAPON_CRIT_BIAS: Partial<Record<ComponentType, number>> = {
  [ComponentType.CANNON_LIGHT]: 0.6,
  [ComponentType.CANNON_HEAVY]: 0.8,
  [ComponentType.BURST_CANNON]: 0.4,
  [ComponentType.LASER]: 1.0,
  [ComponentType.FLAMETHROWER]: 0.2,
  [ComponentType.MISSILE_SAM]: 0.7,
  [ComponentType.MISSILE_SOL_SOL]: 0.9,
  [ComponentType.ROCKET_POD]: 0.3, // low precision = low crit chance
};

const CRIT_COOLDOWN_MS: Partial<Record<ProjectileType, number>> = {
  [ProjectileType.BULLET]: 800,
  [ProjectileType.SHELL]: 0,
  [ProjectileType.MISSILE_ARC]: 0,
  [ProjectileType.FLAME]: 2000,
  [ProjectileType.BEAM]: 0,
};

// Check if a weapon can target this enemy based on air/ground restrictions
export function canWeaponTarget(weaponTarget: WeaponTarget, enemy: Enemy, robotIsFlying: boolean): boolean {
  const enemyFlying = enemy.isFlyer;
  switch (weaponTarget) {
    case WeaponTarget.GROUND:
      return !enemyFlying;                          // ground weapons can't hit flyers
    case WeaponTarget.AIR:
      return enemyFlying;                            // air weapons only hit flyers
    case WeaponTarget.BOTH:
      return true;
    default:
      return !enemyFlying;
  }
}

// Find an enemy visible by any allied robot (shared vision for sol-sol missiles)
export function findSharedVisionTarget(
  robot: Robot,
  allies: Robot[],
  enemies: Enemy[],
  weaponRange: number,
  modifier?: TargetModifier,
): Enemy | null {
  // Collect all enemies seen by any ally's sensor
  const seenEnemies = new Set<Enemy>();
  for (const ally of allies) {
    if (!ally.alive) continue;
    for (const e of enemies) {
      if (!e.alive) continue;
      if (ally.isInSensorCone(e.body.x, e.body.y, ally.sensorRange, e.id)) {
        seenEnemies.add(e);
      }
    }
  }
  // Also include the robot's own sensor
  for (const e of enemies) {
    if (!e.alive) continue;
    if (robot.isInSensorCone(e.body.x, e.body.y, robot.sensorRange, e.id)) {
      seenEnemies.add(e);
    }
  }

  // Filter by weapon range from the firing robot
  const inRange = [...seenEnemies].filter(e =>
    robot.distanceTo(e.body.x, e.body.y) <= weaponRange
  );
  if (inRange.length === 0) return null;

  if (modifier === TargetModifier.MOST_ADVANCED) {
    return inRange.reduce((best, e) => e.body.x > best.body.x ? e : best);
  }
  return inRange.reduce((best, e) => {
    const dBest = robot.distanceTo(best.body.x, best.body.y);
    const dE = robot.distanceTo(e.body.x, e.body.y);
    return dE < dBest ? e : best;
  });
}

export function findEnemyInRange(
  robot: Robot,
  enemies: Enemy[],
  range: number,
  modifier?: TargetModifier,
  enemyHash?: SpatialHash<Enemy>,
): Enemy | null {
  // Use spatial hash to narrow candidates if available, else check all
  const candidates = enemyHash
    ? enemyHash.queryRadius(robot.x, robot.y, range + 20) // +margin for collision radii
    : enemies;

  const inRange = candidates.filter(e =>
    e.alive && !e.isCloaked && robot.isInSensorCone(e.body.x, e.body.y, range, e.id)
  );

  if (inRange.length === 0) return null;

  if (modifier === TargetModifier.MOST_ADVANCED) {
    // Most advanced = furthest X (closest to base on the right)
    return inRange.reduce((best, e) =>
      e.body.x > best.body.x ? e : best
    );
  }

  // Default: closest
  return inRange.reduce((best, e) => {
    const dBest = robot.distanceTo(best.body.x, best.body.y);
    const dE = robot.distanceTo(e.body.x, e.body.y);
    return dE < dBest ? e : best;
  });
}

export function shootAt(robot: Robot, enemy: Enemy, ctx: BehaviorContext): void {
  const weapons = robot.getWeapons();
  if (weapons.length === 0) return;
  const dt = ctx.delta / 1000;
  const robotFlying = robot.hasWings();
  const pm = ctx.projectileManager;

  for (const w of weapons) {
    const range = w.def.weaponRange;
    // Unique cooldown key per component instance (localX+localY makes it unique even for duplicate types)
    const cdKey = `${robot.id}_${w.def.type}_${w.localX}_${w.localY}`;

    // ── Targeting restriction: air/ground ──
    if (!canWeaponTarget(w.def.weaponTarget, enemy, robotFlying)) {
      // Primary target can't be hit by this weapon — find nearest valid alternative
      const validEnemies = ctx.enemies.filter(e =>
        e.alive && !e.isCloaked && canWeaponTarget(w.def.weaponTarget, e, robotFlying),
      );
      const altTarget = w.def.sharedVision
        ? findSharedVisionTarget(robot, ctx.allies, validEnemies, range)
        : findEnemyInRange(robot, validEnemies, range, undefined, ctx.enemyHash);
      if (altTarget) {
        fireWeapon(robot, altTarget, w, cdKey, dt, pm, ctx);
      }
      continue;
    }

    const dist = robot.distanceTo(enemy.body.x, enemy.body.y);
    if (dist > range) continue;

    // ── Detection check: can only fire at detected enemies ──
    // Detected = in sensor cone OR within close-range eyesight
    const inEyesight = dist <= GOD.eyesightRange;
    const inSensor = robot.isInSensorCone(enemy.body.x, enemy.body.y, robot.sensorRange, enemy.id);

    if (w.def.sharedVision) {
      // Shared vision: can fire if any ally detects the enemy
      const anyAllySees = ctx.allies.some(a =>
        a.alive && (
          a.distanceTo(enemy.body.x, enemy.body.y) <= GOD.eyesightRange ||
          a.isInSensorCone(enemy.body.x, enemy.body.y, a.sensorRange, enemy.id)
        )
      );
      if (!inEyesight && !inSensor && !anyAllySees) continue;
    } else {
      if (!inEyesight && !inSensor) continue;
    }

    // ── Wall LOS check: don't fire through walls ──
    // Flying robots shoot from above (no wall block). Arc missiles lob over walls.
    if (!robotFlying && w.def.projectileType !== ProjectileType.MISSILE_ARC) {
      if (!hasLineOfSight(robot.x, robot.y, enemy.body.x, enemy.body.y)) continue;
    }

    fireWeapon(robot, enemy, w, cdKey, dt, pm, ctx);
  }
}

/** Fire all weapons at a static enemy base target. Simpler than shootAt — no movement prediction needed. */
export function shootAtBase(robot: Robot, base: Actor, ctx: BehaviorContext): void {
  const weapons = robot.getWeapons();
  if (weapons.length === 0) return;
  const dt = ctx.delta / 1000;
  const pm = ctx.projectileManager;

  for (const w of weapons) {
    const range = w.def.weaponRange;
    const cdKey = `${robot.id}_${w.def.type}_${w.localX}_${w.localY}`;
    const dist = robot.distanceTo(base.x, base.y);
    if (dist > range) continue;

    // Only ground-targeting weapons can hit bases
    if (w.def.weaponTarget === WeaponTarget.AIR) continue;

    // Wall LOS check (same as shootAt — skip for flyers and arc missiles)
    if (!robot.hasWings() && w.def.projectileType !== ProjectileType.MISSILE_ARC) {
      if (!hasLineOfSight(robot.x, robot.y, base.x, base.y)) continue;
    }

    // Reuse fireWeapon with a fake "enemy" proxy that delegates takeDamage to the base
    const fakeEnemy = {
      body: { x: base.x, y: base.y },
      alive: base.alive,
      takeDamage: (dmg: number) => base.takeDamage(dmg),
      getVelocity: () => ({ vx: 0, vy: 0 }),
      isFlyer: false,
      speed: 0,
      vx: 0,
      vy: 0,
      collisionRadius: base.collisionRadius,
      stun: () => {},           // bases can't be stunned
      knockBack: () => {},      // bases can't be knocked back
      applyBurn: () => {},      // bases can't burn
      id: `base_${base.x}_${base.y}`,
    } as unknown as Enemy;

    fireWeapon(robot, fakeEnemy, w, cdKey, dt, pm, ctx);
  }
}

// Core firing logic for a single weapon (handles cooldowns, energy, projectile spawn)
export function fireWeapon(
  robot: Robot,
  enemy: Enemy,
  w: InstalledComponent,
  cdKey: string,
  dt: number,
  pm?: ProjectileManager,
  ctx?: BehaviorContext,
): void {
  // ── Burst cannon special handling (with overheat) ──
  if (w.def.type === ComponentType.BURST_CANNON) {
    if (!burstState.has(cdKey)) {
      burstState.set(cdKey, { shotsLeft: BURST_COUNT, burstCooldown: 0, heat: 0, overheated: false, overheatTimer: 0 });
    }
    const bs = burstState.get(cdKey)!;

    // ── Overheat state: forced cooldown with visual/audio feedback ──
    if (bs.overheated) {
      bs.overheatTimer -= dt;
      // Heat decays during overheat cooldown
      bs.heat = Math.max(0, bs.heat - BURST_HEAT_DECAY * 2 * dt);
      if (bs.overheatTimer <= 0) {
        bs.overheated = false;
        bs.heat = 0;
        bs.shotsLeft = BURST_COUNT;
      }
      // Emit overheat state for visual feedback (Robot listens)
      EventBus.emit(GameEvents.WEAPON_OVERHEAT, { robot, comp: w, heat: bs.heat / BURST_HEAT_MAX, cooling: true });
      return;
    }

    // Passive heat decay when not overheated
    bs.heat = Math.max(0, bs.heat - BURST_HEAT_DECAY * dt);

    bs.burstCooldown = Math.max(0, bs.burstCooldown - dt * 1000);
    if (bs.burstCooldown > 0) return;

    const energyCost = Math.max(0, -w.def.energy) * (1 / w.def.fireRate);
    if (!robot.consumeEnergy(energyCost)) return;

    // Burst crit propagation: first shot evaluates crit; if it crits, all burst shots inherit
    const isFirstShot = bs.shotsLeft === BURST_COUNT;
    if (isFirstShot) {
      const wPos = robot.getWeaponWorldPos(w);
      const supportDmgMult = robot.isSupportRobot() ? 0.5 : 1;
      const h = computeDamageHeuristic(wPos.x, wPos.y, enemy, w.def.weaponRange) * supportDmgMult;
      const firstCrit = evaluateCrit(robot, h, w.def);
      bs.burstCritActive = firstCrit.isCrit;
    }
    spawnOrInstantDamage(robot, enemy, w, pm, ctx, undefined, undefined, bs.burstCritActive || undefined);
    bs.shotsLeft--;
    bs.heat += BURST_HEAT_PER_SHOT;

    EventBus.emit(GameEvents.ROBOT_FIRED, { robot, enemy, weaponType: w.def.type });

    // Emit heat level for visual feedback (glow intensifies as heat builds)
    if (bs.heat > BURST_HEAT_MAX * 0.5) {
      EventBus.emit(GameEvents.WEAPON_OVERHEAT, { robot, comp: w, heat: bs.heat / BURST_HEAT_MAX, cooling: false });
    }

    if (bs.shotsLeft <= 0) {
      bs.shotsLeft = BURST_COUNT;
      bs.burstCooldown = BURST_PAUSE_MS;
      bs.burstCritActive = false;
    } else {
      bs.burstCooldown = BURST_INTERVAL_MS;
    }

    // Check overheat after firing
    if (bs.heat >= BURST_HEAT_MAX) {
      bs.overheated = true;
      bs.overheatTimer = BURST_OVERHEAT_COOLDOWN;
      bs.burstCooldown = 0;
      SoundManager.play('steamVent');
      EventBus.emit(GameEvents.WEAPON_OVERHEAT, { robot, comp: w, heat: 1.0, cooling: true });
    }
    return;
  }

  // ── Rocket Pod: salvo of multiple missiles with staggered timing + random spread ──
  if (w.def.type === ComponentType.ROCKET_POD) {
    const remaining = fireCooldowns.get(cdKey) ?? 0;
    if (remaining > 0) return;

    const energyCost = Math.max(0, -w.def.energy) * (1 / w.def.fireRate);
    if (!robot.consumeEnergy(energyCost)) return;

    const salvoCount = w.def.salvoCount || 5;
    const SALVO_SPREAD = 30; // ±30px random offset on target position
    const SALVO_STAGGER_MS = 50; // 50ms between each missile in salvo

    for (let i = 0; i < salvoCount; i++) {
      // Use a delayed timer for staggered spawns (first missile fires immediately)
      const delay = i * SALVO_STAGGER_MS;
      const spawnMissile = () => {
        if (!robot.alive || !enemy.alive) return;
        // Random spread offset on target position for low precision
        const offsetX = (Math.random() - 0.5) * 2 * SALVO_SPREAD;
        const offsetY = (Math.random() - 0.5) * 2 * SALVO_SPREAD;
        const wPos = robot.getWeaponWorldPos(w);
        const supportDmgMult = robot.isSupportRobot() ? 0.5 : 1;
        const heuristic = computeDamageHeuristic(wPos.x, wPos.y, enemy, w.def.weaponRange) * supportDmgMult;
        const crit = evaluateCrit(robot, heuristic, w.def);

        if (pm && w.def.projectileSpeed > 0) {
          // Create a fake target position with spread offset for the missile aim point
          const spreadEnemy = {
            body: { x: enemy.body.x + offsetX, y: enemy.body.y + offsetY },
            alive: enemy.alive,
            getVelocity: () => enemy.getVelocity(),
            takeDamage: (dmg: number, pType: ProjectileType, piercing?: boolean, isCrit?: boolean, fromCamo?: boolean) =>
              enemy.takeDamage(dmg, pType, piercing, isCrit, fromCamo),
            stun: (d: number) => enemy.stun(d),
            knockBack: (tiles: number, factor: number) => enemy.knockBack(tiles, factor),
            id: enemy.id,
          } as unknown as Enemy;

          pm.spawn({
            startX: wPos.x,
            startY: wPos.y,
            target: spreadEnemy,
            damage: w.def.damage * GOD.robotDamageMult * heuristic * crit.critMult,
            speed: w.def.projectileSpeed,
            projectileType: w.def.projectileType,
            color: crit.isCrit ? 0xffcc00 : w.def.color,
            isCrit: crit.isCrit,
            piercing: crit.piercing,
            fromCamo: robot.isCamoActive,
            allEnemies: ctx ? () => ctx.enemies.filter(e => e.alive) : undefined,
            homingInterval: Math.max(0.1, 0.5 - w.evoLevel * 0.2),
            isFlyer: robot.canFly(),
          });
        }
      };

      if (delay === 0) {
        spawnMissile();
      } else if (ctx?.scene) {
        ctx.scene.time.delayedCall(delay, spawnMissile);
      }
    }

    fireCooldowns.set(cdKey, 1000 / (w.def.fireRate * robot.terrainFireRateMult));
    EventBus.emit(GameEvents.ROBOT_FIRED, { robot, enemy, weaponType: w.def.type });
    return;
  }

  // ── Flamethrower: continuous fire every tick, damage scaled by dt ──
  if (w.def.type === ComponentType.FLAMETHROWER) {
    const energyCost = Math.max(0, -w.def.energy) * dt; // energy per second × dt
    if (!robot.consumeEnergy(energyCost)) return;

    // Damage scale: def.damage is per-shot at fireRate, convert to per-second × dt
    // DPS = damage * fireRate, so per tick = damage * fireRate * dt
    const dps = w.def.damage * w.def.fireRate;

    // Throttle visual: only redraw flame cone every ~100ms to avoid GC spam
    const visKey = cdKey + '_vis';
    const lastVisual = fireCooldowns.get(visKey) ?? 0;
    const drawVisual = lastVisual <= 0;
    if (drawVisual) fireCooldowns.set(visKey, 100);

    spawnOrInstantDamage(robot, enemy, w, pm, ctx, dps * dt, !drawVisual);

    if (drawVisual) {
      EventBus.emit(GameEvents.ROBOT_FIRED, { robot, enemy, weaponType: w.def.type });
    }
    return;
  }

  // ── Normal weapon cooldown ──
  const remaining = fireCooldowns.get(cdKey) ?? 0;
  if (remaining > 0) return;

  const effectiveFR = w.def.fireRate * robot.terrainFireRateMult;
  const energyCost = Math.max(0, -w.def.energy) * (1 / effectiveFR);
  if (!robot.consumeEnergy(energyCost)) return;

  spawnOrInstantDamage(robot, enemy, w, pm, ctx);
  fireCooldowns.set(cdKey, 1000 / effectiveFR);

  EventBus.emit(GameEvents.ROBOT_FIRED, { robot, enemy, weaponType: w.def.type });
}

/**
 * Compute a damage multiplier based on distance, enemy speed, and aim angle.
 * Returns a value in [0.5, 1.0] that scales the final damage.
 */
export function computeDamageHeuristic(
  weaponX: number, weaponY: number,
  enemy: Enemy,
  weaponRange: number,
): number {
  const dx = enemy.body.x - weaponX;
  const dy = enemy.body.y - weaponY;
  const dist = Math.sqrt(dx * dx + dy * dy);

  // 1) Distance: 100% at point blank, 92% at max range (gentle curve)
  const distRatio = Math.min(1, dist / Math.max(weaponRange, 1));
  const distFactor = 1 - distRatio * distRatio * 0.08;

  // 2) Enemy speed: fast targets are harder to hit cleanly
  // ~100% at speed 0, ~94% at speed 200px/s
  const vel = enemy.getVelocity();
  const speed = Math.sqrt(vel.vx * vel.vx + vel.vy * vel.vy);
  const speedFactor = 1 - Math.min(0.06, speed / 3200);

  // 3) Perpendicular movement: if enemy moves sideways relative to
  //    the line of fire, shots land less cleanly
  let angleFactor = 1;
  if (dist > 1 && speed > 10) {
    const toEnemyX = dx / dist;
    const toEnemyY = dy / dist;
    // dot = cosine of angle between fire line and enemy velocity
    const dot = Math.abs((vel.vx * toEnemyX + vel.vy * toEnemyY) / speed);
    // dot=1 → moving toward/away (easy), dot=0 → perpendicular (harder)
    angleFactor = 0.95 + 0.05 * dot;
  }

  return Math.max(0.7, distFactor * speedFactor * angleFactor);
}

/** Compute precision score (0-1) from build quality + tactical situation. >= CRIT_THRESHOLD = crit. */
export function computePrecisionScore(robot: Robot, heuristic: number, weaponType: ComponentType): number {
  const stabilityFactor = robot.stability.score;
  const directionFactor = Math.min(1, robot.directionAccuracyBonus / 0.7);
  const heuristicFactor = Math.max(0, (heuristic - 0.5) / 0.5);
  const stabLegsFactor = robot.hasStabLegs() ? 1.0 : 0.0;
  const weaponBias = WEAPON_CRIT_BIAS[weaponType] ?? 0.5;

  return stabilityFactor * 0.30
       + directionFactor * 0.25
       + heuristicFactor * 0.25
       + stabLegsFactor  * 0.10
       + weaponBias      * 0.10;
}

/** Evaluate crit for a shot. Returns { isCrit, critMult, piercing }. */
export function evaluateCrit(
  robot: Robot, heuristic: number, def: ComponentDef,
): { isCrit: boolean; critMult: number; piercing: boolean } {
  const score = computePrecisionScore(robot, heuristic, def.type);
  const cdKey = `${robot.id}_crit_${def.type}`;
  const cdRemaining = fireCooldowns.get(cdKey) ?? 0;

  if (score < CRIT_THRESHOLD || cdRemaining > 0) {
    return { isCrit: false, critMult: 1.0, piercing: false };
  }

  const excess = score - CRIT_THRESHOLD;
  const critMult = 1.5 + excess * 1.67; // 1.5x to 2.0x
  fireCooldowns.set(cdKey, CRIT_COOLDOWN_MS[def.projectileType] ?? 0);

  // Piercing: bullet crits ignore 50% of enemy resistance
  const piercing = def.projectileType === ProjectileType.BULLET;

  return { isCrit: true, critMult, piercing };
}

// Spawn a projectile from the weapon's world position (not robot center)
export function spawnOrInstantDamage(
  robot: Robot,
  enemy: Enemy,
  w: InstalledComponent,
  pm?: ProjectileManager,
  ctx?: BehaviorContext,
  damageOverride?: number,
  skipVisual?: boolean,
  forceCrit?: boolean,
): void {
  const def = w.def;
  // Support robots deal 50% less damage (fragile support chassis)
  const supportDmgMult = robot.isSupportRobot() ? 0.5 : 1;

  // All projectiles always launch — misses happen physically (enemy dodges in flight)
  const wPos = robot.getWeaponWorldPos(w);
  const dx = enemy.body.x - wPos.x;
  const dy = enemy.body.y - wPos.y;
  const dist = Math.sqrt(dx * dx + dy * dy);

  // Damage heuristic: distance, enemy speed, aim angle
  const heuristic = computeDamageHeuristic(wPos.x, wPos.y, enemy, def.weaponRange) * supportDmgMult;

  // ── Critical hit evaluation ──
  // forceCrit: burst cannon propagates first shot's crit to remaining burst shots
  const crit = forceCrit
    ? { isCrit: true, critMult: 1.5 + (computePrecisionScore(robot, heuristic, def.type) - CRIT_THRESHOLD) * 1.67, piercing: def.projectileType === ProjectileType.BULLET }
    : evaluateCrit(robot, heuristic, def);
  if (crit.isCrit) {
    EventBus.emit(GameEvents.CRITICAL_HIT, {
      robot, enemy, weaponType: def.type,
      x: enemy.body.x, y: enemy.body.y,
    });
  }

  // ── Laser beam: instant damage + visual beam line ──
  // Evolution level determines beam width and piercing ability:
  //   Lv0: 2px, no pierce  |  Lv1: 4px, pierces  |  Lv2: 8px, pierces
  if (def.projectileType === ProjectileType.BEAM) {
    const evoLevel = w.evoLevel;
    const baseBeamWidth = 2 * Math.pow(2, evoLevel); // 2, 4, 8
    const canPierce = evoLevel >= 1;

    // Primary target damage
    enemy.takeDamage(def.damage * heuristic * crit.critMult, ProjectileType.BEAM, crit.piercing, crit.isCrit, robot.isCamoActive);
    // Overcharge: laser crit stuns for 1s
    if (crit.isCrit) {
      enemy.stun(1.0);
    }

    // ── Piercing: beam continues through enemies behind primary target ──
    // Damage falloff: 60% per enemy pierced (1st pierce = 60%, 2nd = 36%, ...)
    let beamEndX = enemy.body.x;
    let beamEndY = enemy.body.y;
    if (canPierce && ctx) {
      const dx = enemy.body.x - wPos.x;
      const dy = enemy.body.y - wPos.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > 1) {
        const nx = dx / dist;
        const ny = dy / dist;
        const maxRange = def.weaponRange;
        // Extend beam to max weapon range
        beamEndX = wPos.x + nx * maxRange;
        beamEndY = wPos.y + ny * maxRange;
        // Half-width of the beam corridor for hit detection
        const halfW = baseBeamWidth * 0.5;

        // Collect enemies in the beam corridor, sorted by distance
        const pierceTargets: { e: Enemy; d: number }[] = [];
        for (const e of ctx.enemies) {
          if (!e.alive || e === enemy) continue;
          const ex = e.body.x - wPos.x;
          const ey = e.body.y - wPos.y;
          // Project onto beam axis (must be beyond primary target)
          const along = ex * nx + ey * ny;
          if (along <= dist || along > maxRange) continue;
          // Perpendicular distance from beam line
          const perp = Math.abs(ex * (-ny) + ey * nx);
          if (perp <= halfW + 16) { // 16px ≈ average enemy half-size
            pierceTargets.push({ e, d: along });
          }
        }
        pierceTargets.sort((a, b) => a.d - b.d);

        // Apply damage with 60% falloff per pierce
        let pierceMult = 0.6;
        for (const pt of pierceTargets) {
          const pierceDmg = def.damage * heuristic * crit.critMult * pierceMult;
          pt.e.takeDamage(pierceDmg, ProjectileType.BEAM, crit.piercing, false, robot.isCamoActive);
          pierceMult *= 0.6; // diminishing returns
          if (pierceMult < 0.1) break; // stop when damage becomes negligible
        }
      }
    }

    SoundManager.play('laser');
    // Draw laser beam visual
    if (ctx?.scene) {
      const beamColor = crit.isCrit ? 0xffcc00 : 0xff0044;
      const beamWidth = crit.isCrit ? baseBeamWidth + 3 : baseBeamWidth;
      const coreColor = crit.isCrit ? 0xffee88 : 0xff8888;
      const gfx = ctx.scene.add.graphics().setDepth(26);
      gfx.lineStyle(beamWidth, beamColor, 0.9);
      gfx.lineBetween(wPos.x, wPos.y, beamEndX, beamEndY);
      // Bright core
      gfx.lineStyle(Math.max(1, baseBeamWidth * 0.3), coreColor, 1);
      gfx.lineBetween(wPos.x, wPos.y, beamEndX, beamEndY);
      // Impact flash on primary target
      gfx.fillStyle(crit.isCrit ? 0xffcc00 : 0xff2244, 0.8);
      gfx.fillCircle(enemy.body.x, enemy.body.y, crit.isCrit ? 10 : 6);
      ctx.scene.tweens.add({
        targets: gfx,
        alpha: 0,
        duration: 200,
        onComplete: () => gfx.destroy(),
      });
      EventBus.emit(GameEvents.LASER_FIRED);
    }
    return;
  }

  // ── Flamethrower: cone damage + fire visual ──
  if (def.projectileType === ProjectileType.FLAME) {
    const flameDmg = damageOverride ?? def.damage;
    // Damage primary target
    enemy.takeDamage(flameDmg * heuristic * crit.critMult, ProjectileType.FLAME, crit.piercing, crit.isCrit, robot.isCamoActive);
    // Apply burn DOT — Inferno: crit doubles burn duration
    const burnDur = (5 + Math.random() * 5) * (crit.isCrit ? 2 : 1);
    enemy.applyBurn(burnDur, 2);
    SoundManager.play('flame');
    // Also damage other enemies in a short cone (half the weapon range)
    if (ctx) {
      const heading = Math.atan2(enemy.body.y - wPos.y, enemy.body.x - wPos.x);
      const coneHalf = Math.PI / 6; // 30° half = 60° total cone
      for (const e of ctx.enemies) {
        if (!e.alive || e === enemy) continue;
        const dx = e.body.x - wPos.x;
        const dy = e.body.y - wPos.y;
        const distSq = dx * dx + dy * dy;
        if (distSq > def.weaponRange * def.weaponRange) continue;
        let angle = Math.atan2(dy, dx) - heading;
        while (angle > Math.PI) angle -= Math.PI * 2;
        while (angle < -Math.PI) angle += Math.PI * 2;
        if (Math.abs(angle) <= coneHalf) {
          e.takeDamage(flameDmg * 0.5, ProjectileType.FLAME, undefined, undefined, robot.isCamoActive);
          e.applyBurn(burnDur, 2); // secondary targets also catch fire
        }
      }
      // Fire visual: animated flame cone from weapon (skipped in continuous mode, drawn separately)
      if (ctx.scene && !skipVisual) {
        const gfx = ctx.scene.add.graphics().setDepth(25);
        const flameLen = def.weaponRange * 0.7;
        // Draw flame wedge (base glow)
        gfx.fillStyle(0xff6600, 0.25);
        gfx.beginPath();
        gfx.moveTo(wPos.x, wPos.y);
        const segments = 8;
        for (let i = 0; i <= segments; i++) {
          const a = heading - coneHalf + (coneHalf * 2 * i / segments);
          gfx.lineTo(wPos.x + Math.cos(a) * flameLen, wPos.y + Math.sin(a) * flameLen);
        }
        gfx.closePath();
        gfx.fillPath();
        // Inner brighter cone (white-hot on crit)
        gfx.fillStyle(crit.isCrit ? 0xffffff : 0xffaa00, crit.isCrit ? 0.5 : 0.3);
        gfx.beginPath();
        gfx.moveTo(wPos.x, wPos.y);
        for (let i = 0; i <= segments; i++) {
          const a = heading - coneHalf * 0.5 + (coneHalf * i / segments);
          gfx.lineTo(wPos.x + Math.cos(a) * flameLen * 0.6, wPos.y + Math.sin(a) * flameLen * 0.6);
        }
        gfx.closePath();
        gfx.fillPath();
        ctx.scene.tweens.add({
          targets: gfx,
          alpha: 0,
          duration: 250,
          onComplete: () => gfx.destroy(),
        });

        // PNG flame sprites scattered in cone — origin at weapon nozzle
        const FLAME_H_KEYS = ['fire_0_H', 'fire_1_H', 'fire_2_H'];
        const FLAME_V_KEYS = ['fire_3_V', 'fire_4_V'];
        const ALL_FLAME_KEYS = [...FLAME_H_KEYS, ...FLAME_V_KEYS];
        const flameCount = 3 + Math.floor(Math.random() * 3); // 3-5 sprites
        for (let i = 0; i < flameCount; i++) {
          const key = ALL_FLAME_KEYS[Math.floor(Math.random() * ALL_FLAME_KEYS.length)];
          const isH = FLAME_H_KEYS.includes(key);
          // Spread flames across the cone: near nozzle = tiny, far edge = bigger
          const distFrac = 0.05 + Math.random() * 0.95; // 5%-100% of flameLen
          const angleOffset = (Math.random() - 0.5) * 2 * coneHalf * distFrac; // tighter near nozzle
          const spawnAngle = heading + angleOffset;
          const spawnDist = flameLen * distFrac;
          const fx = wPos.x + Math.cos(spawnAngle) * spawnDist;
          const fy = wPos.y + Math.sin(spawnAngle) * spawnDist;

          const sprite = ctx.scene.add.image(fx, fy, key).setDepth(26);
          // Center origin — flame is positioned by its center at the spawn point
          sprite.setOrigin(0.5, 0.5);
          // Rotate to match heading: H (pointing right) → heading, V (pointing up) → heading + PI/2
          sprite.setRotation(isH ? heading : heading + Math.PI / 2);
          // Flip H sprites when heading is roughly leftward (mirror along fire axis)
          if (isH && Math.abs(heading) > Math.PI / 2) {
            sprite.setFlipY(true);
          }
          // Scale proportional to distance: small near nozzle, bigger at edge
          const baseScale = 0.02 + distFrac * 0.18; // 0.02 at nozzle → 0.20 at edge
          const endScale = baseScale * (1.3 + Math.random() * 0.4); // grow 30-70% more
          sprite.setScale(baseScale);
          sprite.setAlpha(0.7 + Math.random() * 0.3);
          ctx.scene.tweens.add({
            targets: sprite,
            scaleX: endScale,
            scaleY: endScale,
            alpha: 0,
            duration: 200 + Math.random() * 300,
            ease: 'Quad.easeOut',
            onComplete: () => sprite.destroy(),
          });
        }
      }
    }
    return;
  }

  if (pm && def.projectileSpeed > 0) {
    const isMissile = def.projectileType === ProjectileType.MISSILE_ARC;
    pm.spawn({
      startX: wPos.x,
      startY: wPos.y,
      target: enemy,
      damage: def.damage * GOD.robotDamageMult * heuristic * crit.critMult,
      speed: def.projectileSpeed,
      projectileType: def.projectileType,
      color: crit.isCrit ? 0xffcc00 : def.color,
      isCrit: crit.isCrit,
      piercing: crit.piercing,
      fromCamo: robot.isCamoActive,
      // Missiles need access to all enemies for AoE stun + knockback
      allEnemies: isMissile && ctx
        ? () => ctx.enemies.filter(e => e.alive)
        : undefined,
      // Homing missile: evo level improves tracking (0.7s → 0.3s)
      homingInterval: isMissile ? Math.max(0.1, 0.5 - w.evoLevel * 0.2) : undefined,
      isFlyer: isMissile ? robot.canFly() : undefined,
    });
  } else {
    // Fallback: instant damage
    enemy.takeDamage(def.damage * GOD.robotDamageMult * heuristic * crit.critMult, def.projectileType, crit.piercing, crit.isCrit, robot.isCamoActive);
  }
}

export function explode(robot: Robot, ctx: BehaviorContext): void {
  const charge = robot.getExplosiveCharge();
  if (!charge) return;

  const evoLevel = charge.evoLevel;
  const aoeRadius = charge.def.explosionRadius * GOD.kamikazeAoeRadiusMult;
  const power = GOD.kamikazeFalloffPower;

  // Damage all enemies in explosion radius (falloff: 100% at center → 0% at edge)
  for (const enemy of ctx.enemies) {
    if (!enemy.alive) continue;
    const dist = robot.distanceTo(enemy.body.x, enemy.body.y);
    if (dist > aoeRadius) continue;
    const t = dist / aoeRadius;
    const falloff = Math.pow(1 - t, power);
    enemy.takeDamage(Math.floor(charge.def.explosionDamage * falloff));
    if (!enemy.alive) continue; // killed → skip secondary effects

    // Lv1+: stun survivors (2s, scaled by proximity)
    if (evoLevel >= 1) {
      enemy.stun(2.0 * (1 - t));
    }
    // Lv2+: incendiary — survivors catch fire (8s burn, 3 dps)
    if (evoLevel >= 2) {
      enemy.applyBurn(8, 3);
    }
    // Lv3: knockback all survivors (3 tiles max, proximity-based)
    if (evoLevel >= 3) {
      enemy.knockBack(3, 1 - t);
    }
  }

  // Damage enemy bases in explosion radius
  const bases = ctx.enemyBases ?? [];
  for (const base of bases) {
    if (!base.alive) continue;
    const dist = robot.distanceTo(base.x, base.y);
    if (dist <= aoeRadius) {
      const t = dist / aoeRadius;
      const falloff = Math.pow(1 - t, power);
      base.takeDamage(Math.floor(charge.def.explosionDamage * falloff));
    }
  }

  // Emit with evo level so VFX can pick nuke animation for Lv3
  EventBus.emit(GameEvents.ROBOT_EXPLODED, { x: robot.x, y: robot.y, evoLevel });
  robot.destroy();
}

export function updateCooldown(robotId: string, delta: number): void {
  for (const [key, val] of fireCooldowns.entries()) {
    if (key.startsWith(robotId)) {
      const newVal = val - delta;
      if (newVal <= 0) {
        fireCooldowns.delete(key);
      } else {
        fireCooldowns.set(key, newVal);
      }
    }
  }
}

export function resetCombatCaches(): void {
  fireCooldowns.clear();
  burstState.clear();
}
